import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import geoViewport from '@mapbox/geo-viewport';

import { LINKS_COLOR } from '../../theme';

const PARIS_CENTER = [3.44970703125, 49.023461463214126];
const DESKTOP_MAP_SIZE = [270, 347];
const TABLET_MAP_SIZE = [800, 347];
const MOBILE_MAP_SIZE = [480, 347];
const ZOOM_REGULATION_TO_HAVE_PADDINGS = 1.5;

const Container = styled.a`
  display: block;
  position: relative;

  img {
    height: 100%;
    width: 100%;
    object-fit: cover;
  }
`;

const MapCaption = styled.span`
  padding: 5px 11px 8px 12px;
  border-radius: 7px;
  box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.05);
  background-color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.2px;
  color: ${LINKS_COLOR};
  position: absolute;
  top: 8px;
  left: 8px;
`;

const MapImage = ({
  coordinates, colors, sizes, link,
}) => {
  const { desktopSize, mobileSize, tabletSize } = sizes;

  const [pointsCenter, setPointsCenter] = useState(PARIS_CENTER);
  const [zoom, setZoom] = useState(0);
  const [isMapDataReady, setMapDataReady] = useState(false);

  useEffect(() => {
    if (coordinates.length) {
      const coordinatesToTurfPoints = coordinates.map((coordinate) => global.turf.point(coordinate));
      const features = global.turf.featureCollection(coordinatesToTurfPoints);

      const bbox = global.turf.bbox(features);
      const mapViewport = geoViewport.viewport(bbox, desktopSize);
      setPointsCenter(mapViewport.center);
      setZoom(mapViewport.zoom - ZOOM_REGULATION_TO_HAVE_PADDINGS);
      setMapDataReady(true);
    }
  }, [coordinates]);

  const generateMarkers = useCallback(() => coordinates.reduce((result, coordinate, i) => (
    `${result}${result.length ? ',' : ''}pin-s+${colors?.length ? colors[i] : LINKS_COLOR.slice(1)}(${coordinate.join(',')})`
  ), ''), [coordinates]);

  const generateMapLink = useCallback((width, height, is2x = false) => `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/${generateMarkers()}/${pointsCenter[0]},${pointsCenter[1]},${zoom}/${width}x${height}${is2x ? '@2x' : ''}?access_token=${process.env.MAPBOX_ACCESS_TOKEN}`,
    [coordinates, zoom, pointsCenter]);

  const generateRetinaMapLink = useCallback((width, height) => generateMapLink(width, height, true), [coordinates, zoom, pointsCenter]);

  return (
    !!coordinates.length && isMapDataReady && (
      <Container href={link} target="_blank" rel="noopener noreferrer">
        <MapCaption>Afficher la carte</MapCaption>
        <picture>
          <source
            media="(min-width: 998px)"
            srcSet={`${generateMapLink(desktopSize[0], desktopSize[1])} 1x, ${generateRetinaMapLink(desktopSize[0], desktopSize[1])} 2x`}
          />
          <source
            media="(min-width: 480px)"
            srcSet={`${generateMapLink(tabletSize[0], tabletSize[1])} 1x, ${generateRetinaMapLink(tabletSize[0], tabletSize[1])} 2x`}
          />
          <img
            src={generateMapLink()}
            srcSet={`${generateMapLink(mobileSize[0], mobileSize[1])} 1x, ${generateRetinaMapLink(mobileSize[0], mobileSize[1])} 2x`}
            alt="Carte des centres d'examen"
          />
        </picture>
      </Container>
    )
  );
};

MapImage.propTypes = {
  coordinates: PropTypes.array,
  colors: PropTypes.array,
  sizes: PropTypes.shape({
    desktopSize: PropTypes.arrayOf(PropTypes.number),
    mobileSize: PropTypes.arrayOf(PropTypes.number),
    tabletSize: PropTypes.arrayOf(PropTypes.number),
  }),
  link: PropTypes.string,
};

MapImage.defaultProps = {
  coordinates: [],
  colors: [],
  sizes: {
    desktopSize: DESKTOP_MAP_SIZE,
    mobileSize: TABLET_MAP_SIZE,
    tabletSize: MOBILE_MAP_SIZE,
  },
  link: '',
};

export { MapImage };
